/**
 * Feature Flags System
 *
 * Provides runtime feature flags with multiple override sources:
 * 1. URL params (highest priority) - enables side-by-side testing
 * 2. localStorage - persists across sessions
 * 3. Default values - fallback
 *
 * IMPORTANT: Flags are read at module load time, not reactively.
 * Changes require page refresh to take effect. This is intentional
 * because React hooks cannot be called conditionally.
 *
 * Usage:
 *   import { getFlag } from '../lib/featureFlags';
 *   if (getFlag('MY_FLAG')) { ... }
 *
 * URL Override:
 *   localhost:5173?ff_MY_FLAG=true
 */

/**
 * Feature flag definitions with default values.
 * Add new flags here with a descriptive comment.
 */
export const FEATURE_FLAGS = {
} as const;

// When FEATURE_FLAGS has entries, narrows to their literal keys.
// When empty, falls back to `string` so the system remains functional.
type FlagKeys = keyof typeof FEATURE_FLAGS;
export type FeatureFlagKey = [FlagKeys] extends [never] ? string : FlagKeys;

/** Flag metadata for display in dev panel */
export const FLAG_DESCRIPTIONS: Record<FeatureFlagKey, string> = {
};

// Cached URLSearchParams for performance
const urlParams = new URLSearchParams(window.location.search);

/**
 * Get the current value of a feature flag.
 * Priority: URL param > localStorage > default
 */
export function getFlag(key: FeatureFlagKey): boolean {
  // 1. Check URL param override (highest priority)
  const urlParam = urlParams.get(`ff_${key}`);
  if (urlParam !== null) {
    return urlParam === 'true';
  }

  // 2. Check localStorage (with try/catch for Safari private mode)
  try {
    const stored = localStorage.getItem(`ff:${key}`);
    if (stored !== null) {
      return stored === 'true';
    }
  } catch {
    // localStorage unavailable (e.g., Safari private mode)
  }

  // 3. Fall back to default
  return (FEATURE_FLAGS as Record<string, boolean>)[key];
}

/**
 * Check if a flag is overridden by URL param.
 * URL-overridden flags cannot be toggled via the dev panel.
 */
export function isUrlOverridden(key: FeatureFlagKey): boolean {
  return urlParams.has(`ff_${key}`);
}

/**
 * Set a flag value in localStorage.
 * Takes effect after page refresh.
 */
export function setFlag(key: FeatureFlagKey, value: boolean): void {
  try {
    localStorage.setItem(`ff:${key}`, String(value));
  } catch {
    // localStorage unavailable
  }
}

/**
 * Clear a flag from localStorage, reverting to default.
 */
export function clearFlag(key: FeatureFlagKey): void {
  try {
    localStorage.removeItem(`ff:${key}`);
  } catch {
    // localStorage unavailable
  }
}

/** Flag info including current value and source */
export interface FlagInfo {
  value: boolean;
  source: 'url' | 'localStorage' | 'default';
}

/**
 * Get all flags with their current values and sources.
 * Used by the dev panel to display flag state.
 */
export function getAllFlags(): Record<FeatureFlagKey, FlagInfo> {
  return (Object.keys(FEATURE_FLAGS) as FeatureFlagKey[]).reduce(
    (acc, key) => {
      const urlParam = urlParams.get(`ff_${key}`);
      let stored: string | null = null;
      try {
        stored = localStorage.getItem(`ff:${key}`);
      } catch {
        // localStorage unavailable
      }

      acc[key] = {
        value: getFlag(key),
        source: urlParam !== null ? 'url' : stored !== null ? 'localStorage' : 'default',
      };
      return acc;
    },
    {} as Record<FeatureFlagKey, FlagInfo>
  );
}

/**
 * Frozen flag values at module load time.
 * Use these in components that need consistent values during the session.
 * Derived dynamically from FEATURE_FLAGS to prevent drift.
 */
export const FLAGS = (Object.keys(FEATURE_FLAGS) as FeatureFlagKey[]).reduce(
  (acc, key) => {
    acc[key] = getFlag(key);
    return acc;
  },
  {} as Record<FeatureFlagKey, boolean>
);
